import React, { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { StoryblokAsset } from "../../../interfaces/Storyblok/StoryblokAsset.interface";
import {
  StickyButtonIconPosition,
  StickyButtonPosition,
} from "../../../interfaces/Storyblok/StoryblokStickyButton.interface";

// Re-exported so both the contact-card and content-agnostic bloks can lean on one import.
export type { StickyButtonIconPosition, StickyButtonPosition };

// Kept in step with the duration-300 classes on the panel so it unmounts only once it has slid out.
export const PANEL_TRANSITION_MS = 300;

export interface PositionConfig {
  container: string;
  rounded: string;
  // Anchors the panel to the same corner as the tab, clear of the tab itself.
  panel: string;
  // Where the panel rests while closed, so it slides out from behind the tab.
  panelClosed: string;
  // When true the label reads bottom-to-top (edge on the right); otherwise top-to-bottom (edge on the left).
  readUpwards: boolean;
}

export const positionConfig: Record<StickyButtonPosition, PositionConfig> = {
  "top-left": {
    container: "top-24 left-0",
    rounded: "rounded-r-[24px]",
    panel: "sm:left-[58px] sm:top-24",
    panelClosed: "-translate-x-full",
    readUpwards: false,
  },
  "top-right": {
    container: "top-24 right-0",
    rounded: "rounded-l-[24px]",
    panel: "sm:right-[58px] sm:top-24",
    panelClosed: "translate-x-full",
    readUpwards: true,
  },
  "bottom-left": {
    container: "bottom-24 left-0",
    rounded: "rounded-r-[24px]",
    panel: "sm:left-[58px] sm:bottom-24",
    panelClosed: "-translate-x-full",
    readUpwards: false,
  },
  "bottom-right": {
    container: "bottom-24 right-0",
    rounded: "rounded-l-[24px]",
    panel: "sm:right-[58px] sm:bottom-24",
    panelClosed: "translate-x-full",
    readUpwards: true,
  },
};

export const EnquiryIcon: React.FC<{ flip?: boolean }> = ({ flip }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    className={classNames("shrink-0", { "-scale-x-100": flip })}
  >
    <path
      d="M23.5312 15.6094L23.7187 15.8906C23.9062 16.125 24 16.4062 24 16.6875C24 17.3906 23.4375 18 22.6875 18L19.5 18L19.5 19.5C19.5 21.9844 17.4844 24 15 24L4.5 24C2.01562 24 -2.1924e-07 21.9844 -3.27835e-07 19.5L-9.83506e-07 4.5C-1.0921e-06 2.01562 2.01562 -2.84807e-07 4.5 -3.93402e-07L15 -8.52372e-07C17.4844 -9.60968e-07 19.5 2.01562 19.5 4.5L19.5 10.125C19.5 10.2187 19.5469 10.2656 19.5937 10.3594L23.5312 15.6094ZM19.5 16.5L22.3125 16.5L18.375 11.25C18.1406 10.9219 18 10.5469 18 10.125L18 4.5C18 2.85937 16.6406 1.5 15 1.5L4.5 1.5C2.85937 1.5 1.5 2.85937 1.5 4.5L1.5 19.5C1.5 21.1406 2.85937 22.5 4.5 22.5L15 22.5C16.6406 22.5 18 21.1406 18 19.5L18 17.625C18 17.0156 18.5156 16.5 19.125 16.5L19.5 16.5ZM9 18.75L9 11.25C9 10.8281 9.32812 10.5 9.75 10.5C10.1719 10.5 10.5 10.8281 10.5 11.25L10.5 18.75C10.5 19.1719 10.1719 19.5 9.75 19.5C9.32812 19.5 9 19.1719 9 18.75ZM9 7.5L9 5.25C9 4.82812 9.32812 4.5 9.75 4.5C10.1719 4.5 10.5 4.82812 10.5 5.25L10.5 7.5C10.5 7.92187 10.1719 8.25 9.75 8.25C9.32812 8.25 9 7.92187 9 7.5ZM13.5 18.75L13.5 15C13.5 14.5781 13.8281 14.25 14.25 14.25C14.6719 14.25 15 14.5781 15 15L15 18.75C15 19.1719 14.6719 19.5 14.25 19.5C13.8281 19.5 13.5 19.1719 13.5 18.75ZM13.5 11.25L13.5 5.25C13.5 4.82812 13.8281 4.5 14.25 4.5C14.6719 4.5 15 4.82812 15 5.25L15 11.25C15 11.6719 14.6719 12 14.25 12C13.8281 12 13.5 11.6719 13.5 11.25Z"
      fill="currentColor"
    />
  </svg>
);

/*-- An Asset field arrives as an object, so reading it as a string put "[object Object]" in the src
     and broke the image. Accepts either shape, and works the same for SVG, PNG and JPEG. --*/
export const getIconUrl = (icon: StoryblokAsset | string | undefined): string | undefined => {
  const filename = typeof icon === "string" ? icon : icon?.filename;

  return filename?.trim() || undefined;
};

export const resolveIconPosition = (
  iconPosition: StickyButtonIconPosition | StickyButtonIconPosition[] | undefined,
): StickyButtonIconPosition => (Array.isArray(iconPosition) ? iconPosition[0] : iconPosition) ?? "leading";

export const resolvePosition = (
  position: StickyButtonPosition | StickyButtonPosition[] | undefined,
): StickyButtonPosition => (Array.isArray(position) ? position[0] : position) ?? "bottom-right";

export interface PanelAnimation {
  isPanelOpen: boolean;
  isPanelMounted: boolean;
  isPanelVisible: boolean;
  openPanel: () => void;
  closePanel: () => void;
  closeButtonRef: React.RefObject<HTMLButtonElement>;
}

/*-- Drives the slide-in panel shared by both sticky bloks.
     isPanelOpen is the intent, isPanelMounted keeps the panel in the DOM until it has slid out,
     and isPanelVisible drives the transition classes one frame after mounting. Also owns the
     Escape-to-close handler and the body scroll lock. --*/
export const usePanelAnimation = (): PanelAnimation => {
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);
  const [isPanelMounted, setIsPanelMounted] = useState<boolean>(false);
  const [isPanelVisible, setIsPanelVisible] = useState<boolean>(false);
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const openPanel = useCallback(() => setIsPanelOpen(true), []);

  useEffect(() => {
    if (isPanelOpen) {
      setIsPanelMounted(true);
      return undefined;
    }

    const timer = window.setTimeout(() => setIsPanelMounted(false), PANEL_TRANSITION_MS);

    return () => window.clearTimeout(timer);
  }, [isPanelOpen]);

  useEffect(() => {
    if (!isPanelMounted || !isPanelOpen) {
      setIsPanelVisible(false);
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      setIsPanelVisible(true);
      closeButtonRef.current?.focus();
    });

    return () => cancelAnimationFrame(frame);
  }, [isPanelMounted, isPanelOpen]);

  useEffect(() => {
    if (!isPanelOpen) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        closePanel();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isPanelOpen, closePanel]);

  return { isPanelOpen, isPanelMounted, isPanelVisible, openPanel, closePanel, closeButtonRef };
};
